// Formatted MOTIS Excel export: a Summary sheet (per-manager counts, outcomes,
// color legend) plus one color-coded tab per manager. Takes the xlsx-js-style
// module as `XS` so it is only downloaded when someone actually exports.

const STATUS = {
  visited: { label: 'Visited', fill: 'C6EFCE', font: '006100', order: 0 },
  claimed: { label: 'Claimed', fill: 'FFEB9C', font: '9C5700', order: 1 },
  unclaimed: { label: 'Unclaimed', fill: 'EDEDED', font: '595959', order: 2 },
};

const HEADER = { font: { bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '1F2937' } },
  alignment: { vertical: 'center', wrapText: true } };
const TITLE = { font: { bold: true, sz: 14 } };
const SUBTITLE = { font: { color: { rgb: '595959' } } };
const BOLD = { font: { bold: true } };
const BORDER = { style: 'thin', color: { rgb: 'D9D9D9' } };
const CELL_BORDER = { top: BORDER, bottom: BORDER, left: BORDER, right: BORDER };

function statusOf(a) {
  if (a.visited) return 'visited';
  if (a.claimed_by) return 'claimed';
  return 'unclaimed';
}

function shortDate(s) {
  if (!s) return '';
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function noteLine(n) {
  const d = new Date(n.created_at);
  const when = isNaN(d) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${when} – ${n.author_name}: ${n.text}`;
}

function counts(appts) {
  const c = { total: appts.length, visited: 0, claimed: 0, unclaimed: 0 };
  for (const a of appts) c[statusOf(a)] += 1;
  return c;
}

const pct = (n, d) => (d ? `${Math.round((n / d) * 100)}%` : '—');

// Tab names: max 31 chars, no []:*?/\ and unique (case-insensitive).
function sheetName(name, used) {
  const base = (name || 'Unknown').replace(/[[\]:*?/\\]/g, ' ').slice(0, 28).trim() || 'Sheet';
  let n = base;
  let i = 2;
  while (used.has(n.toLowerCase())) n = `${base} ${i++}`;
  used.add(n.toLowerCase());
  return n;
}

function setStyle(ws, XS, r, c, style) {
  const addr = XS.utils.encode_cell({ r, c });
  if (!ws[addr]) ws[addr] = { t: 's', v: '' };
  ws[addr].s = { ...(ws[addr].s || {}), ...style };
}

const APPT_COLUMNS = [
  { label: 'Status', width: 11 },
  { label: 'Rep', width: 18 },
  { label: 'Account', width: 28 },
  { label: 'Opp #', width: 12 },
  { label: 'Address', width: 36 },
  { label: 'Zip', width: 7 },
  { label: 'Install', width: 8 },
  { label: 'Window', width: 15 },
  { label: 'Contact', width: 18 },
  { label: 'Phone', width: 15 },
  { label: 'Visited', width: 8 },
  { label: 'Outcome', width: 20 },
  { label: 'Notes', width: 60 },
];

function apptRow(a, withManager) {
  const row = [
    STATUS[statusOf(a)].label,
    a.claimed_by_name || '',
    a.account_name,
    a.opp_number || '',
    a.address,
    a.zip_code,
    shortDate(a.install_date),
    a.arrival_window || '',
    a.contact_name || '',
    a.contact_phone || '',
    a.visited ? shortDate(a.visited_date) : '',
    a.outcome_label || '',
    a.notes.map(noteLine).join('\n'),
  ];
  if (withManager) row.splice(1, 0, a.team_manager_name);
  return row;
}

function sortAppts(appts) {
  return [...appts].sort((x, y) => STATUS[statusOf(x)].order - STATUS[statusOf(y)].order
    || (x.claimed_by_name || '').localeCompare(y.claimed_by_name || '')
    || (x.install_date || '').localeCompare(y.install_date || '')
    || x.account_name.localeCompare(y.account_name));
}

// One color-coded appointment sheet. Rows are tinted by status.
function apptSheet(XS, title, subtitle, appts, withManager) {
  const cols = withManager
    ? [APPT_COLUMNS[0], { label: 'Manager', width: 18 }, ...APPT_COLUMNS.slice(1)]
    : APPT_COLUMNS;
  const sorted = sortAppts(appts);
  const head = 3; // title, subtitle, blank, then header row
  const aoa = [[title], [subtitle], [], cols.map((c) => c.label), ...sorted.map((a) => apptRow(a, withManager))];
  const ws = XS.utils.aoa_to_sheet(aoa);
  const last = cols.length - 1;
  ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: last } }, { s: { r: 1, c: 0 }, e: { r: 1, c: last } }];
  ws['!cols'] = cols.map((c) => ({ wch: c.width }));
  ws['!autofilter'] = { ref: XS.utils.encode_range({ s: { r: head, c: 0 }, e: { r: head + sorted.length, c: last } }) };
  setStyle(ws, XS, 0, 0, TITLE);
  setStyle(ws, XS, 1, 0, SUBTITLE);
  cols.forEach((_, c) => setStyle(ws, XS, head, c, { ...HEADER, border: CELL_BORDER }));

  const notesCol = last;
  const rows = [{ hpt: 22 }, {}, {}, { hpt: 20 }];
  sorted.forEach((a, i) => {
    const r = head + 1 + i;
    const st = STATUS[statusOf(a)];
    for (let c = 0; c <= last; c++) {
      setStyle(ws, XS, r, c, {
        fill: { fgColor: { rgb: st.fill } },
        border: CELL_BORDER,
        alignment: { vertical: 'top', wrapText: c === notesCol || c === notesCol - 1 },
        ...(c === 0 ? { font: { bold: true, color: { rgb: st.font } } } : {}),
      });
    }
    // Row height so wrapped notes are readable everywhere (Excel, Sheets, Numbers).
    const chars = cols[notesCol].width;
    const lines = a.notes.reduce((n, note) => n + Math.max(1, Math.ceil(noteLine(note).length / chars)), 0);
    rows.push(lines > 1 ? { hpt: Math.min(15 * lines, 180) } : {});
  });
  ws['!rows'] = rows;
  return ws;
}

function summarySheet(XS, weekLabel, groups) {
  const multiDirector = new Set(groups.map((g) => g.director)).size > 1;
  const head = ['Manager', 'Team', 'Total', 'Visited', 'Claimed', 'Unclaimed', 'Visit Rate'];
  if (multiDirector) head.unshift('Director');
  const aoa = [
    [`MOTIS Summary — Week of ${weekLabel}`],
    [`Exported ${new Date().toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}`],
    [],
    head,
  ];
  const tot = { total: 0, visited: 0, claimed: 0, unclaimed: 0 };
  for (const g of groups) {
    const c = counts(g.appts);
    Object.keys(tot).forEach((k) => { tot[k] += c[k]; });
    const row = [g.manager, g.team, c.total, c.visited, c.claimed, c.unclaimed, pct(c.visited, c.total)];
    if (multiDirector) row.unshift(g.director);
    aoa.push(row);
  }
  const totalRow = ['', tot.total, tot.visited, tot.claimed, tot.unclaimed, pct(tot.visited, tot.total)];
  if (multiDirector) totalRow.unshift('');
  totalRow.unshift('Total');
  aoa.push(totalRow);
  const totalR = aoa.length - 1;

  // Outcomes of visited appointments
  const outcomes = {};
  groups.forEach((g) => g.appts.forEach((a) => {
    if (a.visited) outcomes[a.outcome_label || 'Not recorded'] = (outcomes[a.outcome_label || 'Not recorded'] || 0) + 1;
  }));
  aoa.push([], ['Visit Outcomes', 'Count']);
  const outcomeHeadR = aoa.length - 1;
  const outcomeRows = Object.entries(outcomes).sort((a, b) => b[1] - a[1]);
  if (outcomeRows.length === 0) aoa.push(['No visits yet', '']);
  outcomeRows.forEach(([label, n]) => aoa.push([label, n]));

  aoa.push([], ['Color Key']);
  const legendR = aoa.length;
  aoa.push(['Visited', 'Rep went to the appointment']);
  aoa.push(['Claimed', 'A rep picked it but has not visited yet']);
  aoa.push(['Unclaimed', 'Nobody has picked it']);

  const ws = XS.utils.aoa_to_sheet(aoa);
  const last = head.length - 1;
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: last } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: last } },
    // Legend descriptions span the rest of the row so no column gets stretched.
    ...[0, 1, 2].map((i) => ({ s: { r: legendR + i, c: 1 }, e: { r: legendR + i, c: last } })),
  ];
  ws['!cols'] = head.map((h) => ({ wch: ['Director', 'Manager'].includes(h) ? 24 : h === 'Team' ? 8 : 11 }));

  setStyle(ws, XS, 0, 0, TITLE);
  setStyle(ws, XS, 1, 0, SUBTITLE);
  head.forEach((_, c) => setStyle(ws, XS, 3, c, { ...HEADER, border: CELL_BORDER }));
  for (let r = 4; r <= totalR; r++) {
    for (let c = 0; c <= last; c++) {
      setStyle(ws, XS, r, c, { border: CELL_BORDER, ...(r === totalR ? { ...BOLD, fill: { fgColor: { rgb: 'F2F2F2' } } } : {}) });
    }
  }
  [0, 1].forEach((c) => setStyle(ws, XS, outcomeHeadR, c, { ...HEADER, border: CELL_BORDER }));
  setStyle(ws, XS, legendR - 1, 0, BOLD);
  ['visited', 'claimed', 'unclaimed'].forEach((k, i) => {
    setStyle(ws, XS, legendR + i, 0, { fill: { fgColor: { rgb: STATUS[k].fill } }, font: { bold: true, color: { rgb: STATUS[k].font } }, border: CELL_BORDER });
  });
  return ws;
}

// Build the whole workbook. `appts` = everything in the viewer's Team View for
// the week; tabs are one per team manager (the FSM for BAE rows, the R2
// manager for passed rows).
export function buildMotisWorkbook(XS, appts, weekLabel) {
  const byManager = {};
  for (const a of appts) {
    const key = `${a.team_manager_name}|${a.stage}`;
    if (!byManager[key]) {
      byManager[key] = {
        manager: a.team_manager_name,
        director: a.team_director_name || '',
        team: a.stage === 'R2' ? 'R2' : 'BAE',
        appts: [],
      };
    }
    byManager[key].appts.push(a);
  }
  const groups = Object.values(byManager).sort((x, y) => x.director.localeCompare(y.director)
    || x.manager.localeCompare(y.manager));

  const wb = XS.utils.book_new();
  const used = new Set(['summary', 'all appointments']);
  XS.utils.book_append_sheet(wb, summarySheet(XS, weekLabel, groups), 'Summary');
  for (const g of groups) {
    const c = counts(g.appts);
    const subtitle = `Week of ${weekLabel}  ·  ${c.total} total  ·  ${c.visited} visited  ·  ${c.claimed} claimed  ·  ${c.unclaimed} unclaimed`;
    const title = `${g.manager} — ${g.team === 'R2' ? 'R2 team' : 'BAE team'}`;
    XS.utils.book_append_sheet(wb, apptSheet(XS, title, subtitle, g.appts, false), sheetName(g.manager, used));
  }
  if (groups.length > 1) {
    const c = counts(appts);
    const subtitle = `Week of ${weekLabel}  ·  ${c.total} total  ·  ${c.visited} visited  ·  ${c.claimed} claimed  ·  ${c.unclaimed} unclaimed`;
    XS.utils.book_append_sheet(wb, apptSheet(XS, 'All Appointments', subtitle, appts, true), 'All Appointments');
  }
  return wb;
}
